import { useMemo } from 'react'
import { t } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useMergePatientsMutation } from '@/core/services/patient'
import { Patient } from '@/core/models/patient.model'

import { buildMergeOverrides } from '../lib/buildMergeOverrides'
import { FieldGroups, useFieldGroups } from './useFieldGroups'
import { zodResolver } from '@hookform/resolvers/zod'
import { getApiErrorMessage } from '@/utils/errors'
import { mapKeys } from 'lodash'

export type MergePatientsFormSelection = Record<string, 'primary' | 'secondary'>

export type MergePatientsFormData = z.infer<ReturnType<typeof useFormSchema>>

function useFormSchema(fieldGroups: FieldGroups) {
  return useMemo(() => {
    const allFields = fieldGroups.flatMap((group) => group.fields)
    const schemaShape = Object.fromEntries(allFields.map((field) => [field.key, z.enum(['primary', 'secondary'])]))
    return z.object({
      fields: z.object(schemaShape),
      parentIds: z.array(z.string()),
    })
  }, [fieldGroups])
}

type UseMergePatientsFormProps = {
  onSubmitSuccess?: () => void
  primaryPatient: Patient
  secondaryPatient: Patient
}

export function useMergePatientsForm({ onSubmitSuccess, primaryPatient, secondaryPatient }: UseMergePatientsFormProps) {
  const { enqueueSnackbar } = useSnackbar()
  const fieldGroups = useFieldGroups()

  const [mergePatients, { isLoading: isMerging }] = useMergePatientsMutation()

  const defaultValues: MergePatientsFormData = useMemo(() => {
    const fields = fieldGroups.map((group) => group.fields).flat()
    return {
      fields: Object.fromEntries(fields.map((field) => [field.key, 'primary' as const])),
      parentIds: [] as string[],
    }
  }, [fieldGroups])
  const schema = useFormSchema(fieldGroups)
  const rhForm = useForm<MergePatientsFormData>({ defaultValues, resolver: zodResolver(schema) })

  const submitHandler: SubmitHandler<MergePatientsFormData> = async (data) => {
    console.log('🚀 ~ submitHandler ~ data:', data)
    try {
      const selection = mapKeys(data.fields, (_value, key) => key.split('_').join('.'))
      console.log('🚀 ~ submitHandler ~ selection:', selection)
      const overrides = buildMergeOverrides(selection, primaryPatient, secondaryPatient, fieldGroups)
      await mergePatients({
        primaryPatient: primaryPatient._id,
        secondaryPatient: secondaryPatient._id,
        overrides,
        parentIds: data.parentIds,
      }).unwrap()
      onSubmitSuccess?.()
    } catch (error) {
      console.log('🚀 ~ submitHandler ~ error:', error)
      enqueueSnackbar(getApiErrorMessage(error, t`An error occurred while merging patients`), { variant: 'error' })
    }
  }

  return {
    isMerging,
    fieldGroups,
    rhForm,
    submitHandler,
  }
}
